
if (!customElements.get('ly-video')) {
    customElements.define('ly-video', class extends HTMLElement {
        #videoAttributeNames = new Set([
            'autoplay',
            'controls',
            'controlslist',
            'crossorigin',
            'disablepictureinpicture',
            'disableremoteplayback',
            'height',
            'loop',
            'muted',
            'playsinline',
            'poster',
            'preload',
            'src',
            'width'
        ])
        #booleanVideoAttributeNames = new Set([
            'autoplay',
            'controls',
            'disablepictureinpicture',
            'disableremoteplayback',
            'loop',
            'muted',
            'playsinline'
        ])
        #booleanVideoPropertyNames = new Map([
            ['autoplay', 'autoplay'],
            ['controls', 'controls'],
            ['disablepictureinpicture', 'disablePictureInPicture'],
            ['disableremoteplayback', 'disableRemotePlayback'],
            ['loop', 'loop'],
            ['muted', 'muted'],
            ['playsinline', 'playsInline']
        ])
        #renderObserver = null
        #renderRootMargin = '200px 0px'
        #visibilityObserver = null
        #isVisibleEnough = false
        #resumePlaybackOnVisible = false
        #visibilityThreshold = 0.2
        #mobileMedia = '(max-width: 767px)'
        #desktopMedia = '(min-width: 768px)'

        connectedCallback() {
            this.#observeRender()
        }

        disconnectedCallback() {
            this.#disconnectRenderObserver()
            this.#disconnectVisibilityObserver()
        }

        render = () => {
            const data = this.#readVideoData()
            if (!data?.sourceGroups?.length) return

            this.#disconnectVisibilityObserver()
            this.querySelector('[data-ly-video-rendered]')?.remove()

            const video = document.createElement('video')
            video.dataset.lyVideoRendered = true

            this.#copyAttributes(video)
            this.#applyDefaultAttributes(video, data.posterData)
            this.#appendSourceGroups(video, data.sourceGroups)
            this.#prepareVideo(video)

            this.appendChild(video)
            this.#observeVisibility()
        }

        #observeRender = () => {
            if (this.querySelector('[data-ly-video-rendered]')) {
                this.#observeVisibility()
                return
            }

            if (!window.IntersectionObserver) {
                this.render()
                return
            }

            this.#renderObserver = new IntersectionObserver(this.#handleRenderVisibilityChange, {
                rootMargin: this.#renderRootMargin,
                threshold: 0
            })
            this.#renderObserver.observe(this)
        }

        #disconnectRenderObserver = () => {
            this.#renderObserver?.disconnect()
            this.#renderObserver = null
        }

        #handleRenderVisibilityChange = entries => {
            const [entry] = entries
            if (!entry.isIntersecting) return

            this.#disconnectRenderObserver()
            this.render()
        }

        #readVideoData = () => {
            const mobileData = this.#parseVideoData(this.dataset.videoMobile)
            const desktopData = this.#parseVideoData(this.dataset.videoDesktop)

            const hasMobile = mobileData?.sources?.length
            const hasDesktop = desktopData?.sources?.length

            if (hasMobile || hasDesktop) {
                const sourceGroups = []

                if (hasMobile) {
                    sourceGroups.push({
                        media: this.#mobileMedia,
                        sources: mobileData.sources
                    })
                }

                if (hasDesktop) {
                    sourceGroups.push({
                        media: this.#desktopMedia,
                        sources: desktopData.sources
                    })
                }

                return {
                    posterData: mobileData || desktopData,
                    sourceGroups
                }
            }

            const data = this.#parseVideoData(this.dataset.video)
            if (!data?.sources?.length) return null

            return {
                posterData: data,
                sourceGroups: [
                    {
                        sources: data.sources
                    }
                ]
            }
        }

        #parseVideoData = raw => {
            if (!raw) return null

            try {
                return JSON.parse(raw)
            } catch (error) {
                console.warn('ly-video json parse failed', error)
                return null
            }
        }

        #copyAttributes = video => {
            for (const attr of this.attributes) {
                if (!this.#videoAttributeNames.has(attr.name)) continue

                if (this.#booleanVideoAttributeNames.has(attr.name)) {
                    this.#setBooleanAttribute(video, attr.name)
                    continue
                }

                video.setAttribute(attr.name, attr.value)
            }
        }

        #applyDefaultAttributes = (video, data) => {
            this.#setBooleanAttribute(video, 'playsinline')

            const poster = data.preview_image?.src || data.previewImage?.src
            if (poster && !video.hasAttribute('poster')) {
                video.setAttribute('poster', poster)
            }
        }

        #setBooleanAttribute = (video, name) => {
            video.setAttribute(name, '')

            const propertyName = this.#booleanVideoPropertyNames.get(name)
            if (propertyName && propertyName in video) {
                video[propertyName] = true
            }

            if (name === 'muted') {
                video.defaultMuted = true
            }
        }

        #appendSourceGroups = (video, sourceGroups) => {
            for (const sourceGroup of sourceGroups) {
                this.#appendSources(video, sourceGroup.sources, sourceGroup.media)
            }
        }

        #appendSources = (video, sources, media) => {
            this.#sortSources(sources).forEach(sourceData => {
                const source = document.createElement('source')
                source.src = sourceData.url
                source.type = this.#getMimeType(sourceData)
                if (media) source.media = media
                video.appendChild(source)
            })
        }

        #prepareVideo = video => {
            this.#bindReadyPlayback(video)

            if (video.preload !== 'none') {
                this.#loadVideo(video)
            }
        }

        #bindReadyPlayback = video => {
            const handleReady = () => {
                if (!this.#isVisibleEnough) return
                if (!video.hasAttribute('autoplay') && !this.#resumePlaybackOnVisible) return

                this.#resumePlaybackOnVisible = false
                this.#playVideo(video)
            }

            video.addEventListener('loadeddata', handleReady, { once: true })
            video.addEventListener('canplay', handleReady, { once: true })
        }

        #loadVideo = video => {
            try {
                video.load()
            } catch (error) {
                console.warn('ly-video load failed', error)
            }
        }

        #observeVisibility = () => {
            if (!window.IntersectionObserver) return

            this.#visibilityObserver = new IntersectionObserver(this.#handleVisibilityChange, {
                threshold: [0, this.#visibilityThreshold]
            })
            this.#visibilityObserver.observe(this)
        }

        #disconnectVisibilityObserver = () => {
            this.#visibilityObserver?.disconnect()
            this.#visibilityObserver = null
        }

        #handleVisibilityChange = entries => {
            const [entry] = entries
            const video = this.querySelector('[data-ly-video-rendered]')
            if (!video) return

            const isHidden = !entry.isIntersecting || entry.intersectionRatio <= 0
            const isBelowVisibilityThreshold = entry.intersectionRatio < this.#visibilityThreshold

            if (isHidden || isBelowVisibilityThreshold) {
                this.#isVisibleEnough = false
                this.#resumePlaybackOnVisible = !video.paused && !video.ended
                video.pause()
                return
            }

            this.#isVisibleEnough = true

            if (!this.#resumePlaybackOnVisible && !video.hasAttribute('autoplay')) return

            this.#resumePlaybackOnVisible = false
            this.#playWhenReady(video)
        }

        #playWhenReady = video => {
            if (video.readyState >= 2) {
                this.#playVideo(video)
                return
            }

            video.addEventListener('canplay', () => {
                if (this.#isVisibleEnough) {
                    this.#playVideo(video)
                }
            }, { once: true })

            if (video.preload !== 'none') {
                this.#loadVideo(video)
            }
        }

        #playVideo = video => {
            video.play()?.catch(() => { })
        }

        #sortSources = sources => {
            const validSources = sources.filter(source => source?.url && this.#getMimeType(source))

            const hlsSources = validSources.filter(source => this.#isHls(source))
            const mp4Sources = validSources
                .filter(source => this.#isMp4(source))
                .sort((a, b) => this.#getHeight(a) - this.#getHeight(b))

            return [...hlsSources, ...mp4Sources]
        }

        #getMimeType = source => source.mime_type || source.mimeType || ''

        #getFormat = source => source.format || ''

        #getHeight = source => Number(source.height) || 0

        #isHls = source => {
            const mimeType = this.#getMimeType(source).toLowerCase()
            const format = this.#getFormat(source).toLowerCase()

            return mimeType === 'application/x-mpegurl'
                || mimeType === 'application/vnd.apple.mpegurl'
                || format === 'm3u8'
                || source.url.includes('.m3u8')
        }

        #isMp4 = source => {
            const mimeType = this.#getMimeType(source).toLowerCase()
            const format = this.#getFormat(source).toLowerCase()

            return mimeType === 'video/mp4'
                || format === 'mp4'
                || source.url.includes('.mp4')
        }
    })
}
